<template>
    <v-dialog
        v-model="showDialog"
        width="600"
    >
        <v-card style="padding: 20px;">
            <v-fade-transition hide-on-leave>
                <div v-show="!isProceedToRevalidate && !showPromptValidityDays.status">
                    <h1>Revalidate Approval</h1>
                    <p>You are about to request revalidation for this approval.</p>
                    <v-alert
                        v-show="errors.length === 0"
                        text
                        prominent
                        type="info"
                        color="teal darken-1"
                    >
                        This approval is good for revalidation. Do you want to proceed?
                    </v-alert>
                    <v-alert
                        v-for="(warning, index) in warnings"
                        v-show="warnings.length > 0"
                        :key="index"
                        text
                        prominent
                        type="warning"
                    >
                        {{ warning }}
                    </v-alert>
                    <v-alert
                        v-for="(error, index) in errors"
                        v-show="errors.length > 0"
                        :key="index"
                        text
                        prominent
                        type="error"
                    >
                        {{ error }}
                    </v-alert>
                    <v-card-actions style="padding-right: 0;">
                        <v-spacer />
                        <v-btn
                            width="120"
                            outlined
                            color="teal darken-1"
                            @click="showDialog = false"
                        >
                            cancel
                        </v-btn>
                        <v-btn
                            :disabled="errors.length > 0"
                            width="120"
                            color="teal darken-1"
                            class="white--text"
                            @click="showPromptValidityDays.status = true"
                        >
                            proceed
                        </v-btn>
                    </v-card-actions>
                </div>
            </v-fade-transition>
            <v-fade-transition hide-on-leave>
                <v-form
                    v-show="showPromptValidityDays.status && !loading"
                    ref="form"
                    v-model="form"
                    @submit.prevent="handleSubmitValidityDays"
                >
                    <h1>Revalidate Approval</h1>
                    <p>Please select the validity end date for the revalidation of this approval.</p>
                    <v-menu
                        v-model="showPromptValidityDays.menu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                    >
                        <template #activator="{ on, attrs }">
                            <v-text-field
                                v-model="showPromptValidityDays.value"
                                outlined
                                label="Select your preferred date"
                                :prepend-inner-icon="mdiCalendar"
                                readonly
                                :rules="validDateRule.concat([
                                    value => aexp_d && aexp_d.isSameOrAfter(moment(value).utcOffset('+0800').startOf('day')) || 'The date is beyond the account expiry date.',
                                    value => (last_d && last_d.isSameOrAfter(moment(value).utcOffset('+0800').startOf('day'))) || (grace_d && grace_d.isSameOrAfter(moment(value).utcOffset('+0800').startOf('day'))) || 'The date is beyond the member expiry date or grace period.',
                                    value => (pdun_d && pdun_d.isSameOrAfter(moment(value).utcOffset('+0800').startOf('day'))) || (grace_d && grace_d.isSameOrAfter(moment(value).utcOffset('+0800').startOf('day'))) || 'The date is beyond the paid until date or grace period.',
                                    value => !canc_d || canc_d.isAfter(moment(value).utcOffset('+0800').startOf('day')) || 'The date is on or beyond the member cancellation date.'
                                ])"
                                v-bind="attrs"
                                color="teal"
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="showPromptValidityDays.value"
                            :min="minDate"
                            :allowed-dates="isAllowedDate"
                            color="teal"
                            header-color="teal"
                            @input="showPromptValidityDays.menu=false"
                        />
                    </v-menu>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            width="120"
                            outlined
                            color="teal darken-1"
                            @click="handleCancelPromptRevalidateApproval"
                        >
                            cancel
                        </v-btn>
                        <v-btn
                            :disabled="errors.length > 0"
                            width="120"
                            color="teal darken-1"
                            class="white--text"
                            type="submit"
                        >
                            revalidate
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-fade-transition>
            <v-fade-transition hide-on-leave>
                <div v-show="isProceedToRevalidate && loading">
                    <h1>Revalidate Approval</h1>
                    <v-progress-linear
                        style="margin: 70px 0;"
                        color="teal darken-1"
                        indeterminate
                        rounded
                        height="8"
                    />
                </div>
            </v-fade-transition>
            <v-fade-transition hide-on-leave>
                <div v-show="isProceedToRevalidate && !loading && isAcuHasResult">
                    <h1>{{ acuResult && acuResult.status !== 200 ? "Unable to Revalidate" : 'Revalidation Completed' }}</h1>
                    <p>{{ acuResult && acuResult.status !== 200 ? "The revalidation request cannot be processed at this time." : 'The revalidation request has been successfully processed.' }}</p>
                    <v-alert text prominent :type="acuResult && acuResult.status !== 200 ? 'error' : 'success'">
                        <div v-if="acuResult && acuResult.status !== 200">
                            <ul v-if="acuResult && acuResult.data.reason.length > 1">
                                <li
                                    v-for="(item, key) in acuResult && acuResult.data.reason"
                                    :key="key"
                                    style="margin: 5px 0;"
                                >
                                    {{ item }}
                                </li>
                            </ul>
                            <span v-else>{{ acuResult && acuResult.data.reason[0] }}</span>
                        </div>
                        <div v-else>
                            <span>The member's approval has been successfully revalidated.</span>
                        </div>
                    </v-alert>
                </div>
            </v-fade-transition>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import {
    mdiCalendar
} from '@mdi/js'
import { moment } from '../../../../utils/date-shim'
export default {
    props: ['show', 'acuResult', 'acuData'],
    data () {
        return {
            mdiCalendar,
            minDate: moment().format('YYYY-MM-DD'),
            form: true,
            showPromptValidityDays: {
                menu: false,
                isSubmitted: true,
                status: false,
                value: moment().utcOffset('+0800').startOf('day').format('YYYY-MM-DD')
            },
            loading: false,
            isProceedToRevalidate: false,
            errors: [],
            warnings: [],
            validDateRule: [
                value => {
                    const regex = /^\d{4}-\d{2}-\d{2}$/
                    return regex.test(value) || 'Date must be in YYYY-MM-DD format'
                },
                value => moment(value).isSameOrAfter(moment().utcOffset('+0800').startOf('day')) || 'Validity Date must be today or a future date'
            ],
            aexp_d: null,
            last_d: null,
            pdun_d: null,
            canc_d: null
        }
    },
    computed: {
        ...mapGetters(['revalidateAcuApprovalData']),
        showDialog: {
            get() {
                return this.show
            },
            set() {
                this.handleCloseDialog()
            }
        }
    },
    watch: {
        acuData(newVal) {
            if (newVal && newVal.diagnostics && Array.isArray(newVal.diagnostics) && newVal.diagnostics.length) {
                let today = moment().utcOffset('+0800').startOf('day')
                let is_account_expired = moment(newVal.diagnostics[0].AEXP_D).utcOffset('+0800').startOf('day').isBefore(today)
                let is_member_expired = moment(newVal.diagnostics[0].LAST_D).utcOffset('+0800').startOf('day').isBefore(today)
                let is_member_unpaid = moment(newVal.diagnostics[0].PDUN_D).utcOffset('+0800').startOf('day').isBefore(today)
                let on_grace_period = newVal.diagnostics[0].GRACE_D && moment(newVal.diagnostics[0].GRACE_D).utcOffset('+0800').startOf('day').isSameOrAfter(today)
                let errors = []
                if (String(newVal.diagnostics[0].STTS_C).split(' ')[0] !== 'Active') {
                    errors.push('The member is not active.')
                }
                if(is_account_expired) {
                    errors.push('The member\'s account has expired.')
                }
                if (!on_grace_period && is_member_expired) {
                    errors.push('The member\'s membership has epired')
                }
                if (!on_grace_period && is_member_unpaid) {
                    errors.push('The member\'s payment has lapsed.')
                }
                if(this.checkIfVerified(newVal.diagnostics)) {
                    errors.push('This approval has already been received and verified by the provider.')
                }
                if (this.checkIfDone(newVal.diagnostics)) {
                    errors.push('There are procedures that are already marked done by the provider.')
                }
                if (today.isBefore(moment(newVal.diagnostics[0].APPR_D).utcOffset('+0800').startOf('day'))){
                    errors.push('This approval is scheduled on a future date.')
                }
                if (today.isSameOrBefore(moment(newVal.diagnostics[0].VALI_D).utcOffset('+0800').startOf('day'))) {
                    errors.push('This approval is still valid.')
                }

                this.errors = errors

                let warnings = []
                if (newVal.diagnostics[0].STTS_C !== 'Active') {
                    warnings.push(`Member Status: ${newVal.diagnostics[0].STTS_C}.`)
                }
                if (on_grace_period && !is_account_expired){
                    warnings.push('This member is on grace period.')
                }
                if ((String(newVal.diagnostics[0].INTREM).toUpperCase().includes('REVALIDATED') || String(newVal.diagnostics[0].HISTORY).toUpperCase().includes('REVALIDATED'))) {
                    warnings.push('This approval has been previously revalidated.')
                }
                this.warnings = warnings

                this.aexp_d = this.isValidDate(newVal.diagnostics[0].AEXP_D) ? moment(newVal.diagnostics[0].AEXP_D).utcOffset('+0800').startOf('day') : null
                this.last_d = this.isValidDate(newVal.diagnostics[0].LAST_D) ? moment(newVal.diagnostics[0].LAST_D).utcOffset('+0800').startOf('day') : null
                this.pdun_d = this.isValidDate(newVal.diagnostics[0].PDUN_D) ? moment(newVal.diagnostics[0].PDUN_D).utcOffset('+0800').startOf('day') : null
                this.grace_d = this.isValidDate(newVal.diagnostics[0].GRACE_D) ? moment(newVal.diagnostics[0].GRACE_D).utcOffset('+0800').startOf('day') : null
                this.canc_d = String(newVal.diagnostics[0].STTS_C).toUpperCase().includes('FOR CANCELLATION') && this.isValidDate(newVal.diagnostics[0].STTS_C) ? moment(this.acuData.diagnostics[0].STTS_D).utcOffset('+0800').startOf('day') : null
            }
        },
        revalidateAcuApprovalData() {
            this.loading = false
        }
    },
    methods: {
        isAllowedDate(value) {
            const today = moment().utcOffset('+0800').startOf('day')
            const date = moment(value).utcOffset('+0800').startOf('day')

            // Disable dates before today
            if (date.isBefore(today)) {
                return false
            }

            // Disable dates beyond Account Expiry
            if (this.aexp_d && date.isAfter(this.aexp_d)) {
                return false
            }

            // Disable dates on and beyond cancellation date
            if (this.canc_d && date.isSameOrAfter(this.canc_d)) {
                return false
            }

            // Disable dates beyond paid until
            if (this.pdun_d && date.isAfter(this.pdun_d)) {
                // Excempt dates within grace period
                if(this.grace_d && date.isSameOrBefore(this.grace_d)) {
                    return true
                } else {
                    // Disable all remaining dates
                    return false
                }
            }

            // if (this.last_d && date.isAfter(this.last_d) || this.grace_d && date.isAfter(this.grace_d)) {
            //     return false;
            // }

            // Allow other dates
            return true
        },
        handleSubmitValidityDays() {
            if (this.$refs && this.$refs.form.validate()) {
                this.showPromptValidityDays.status = false
                this.loading = true
                this.isProceedToRevalidate = true
                this.isAcuHasResult = true
                this.$emit('revalidateApproval', this.showPromptValidityDays.value)
            }
        },
        handleCancelPromptRevalidateApproval() {
            this.showPromptValidityDays = {
                status: false,
                value: moment().utcOffset('+0800').startOf('day').format('YYYY-MM-DD')
            }
            this.showDialog = false
        },
        handlePromptValidatyDays() {
            this.showPromptValidityDays.status = true
        },
        handleCloseDialog() {
            this.showPromptValidityDays = {
                status: false,
                value: moment().utcOffset('+0800').startOf('day').format('YYYY-MM-DD')
            }
            this.$emit('closeDialog')
            setTimeout(()=>{
                this.loading = false
                this.isProceedToRevalidate = false
                this.isAcuHasResult = false
            }, 300)
        },
        checkIfVerified (diagnostics) {
            for (let i = 0; i < diagnostics.length; i++) {
                if (String(diagnostics[i].VERIFIED).trim().toUpperCase() === 'YES') {
                    return true
                }
            }
            return false
        },
        checkIfDone(diagnostics) {
            for (let i = 0; i < diagnostics.length; i++) {
                if (String(diagnostics[i].DONE).trim().toUpperCase() === 'YES') {
                    return true
                }
            }
            return false
        },
        isValidDate(datestring) {
            let date = new Date(datestring)
            return !isNaN(date.getTime())
        }
    }
}
</script>

<style scoped>
h1 {
    font-size: 19px;
    text-align: center;
    color: #2C3E50;
    margin-bottom: 10px;
}
p {
    color: #7F8C8D;
    text-align: center
}
</style>
